'use client';

import { useCallback, useEffect, useState } from 'react';
import { Music4, Plus, VolumeX, X } from 'lucide-react';
import { useBlockModalSpeakerStore } from '@/stores/useBlockModalSpeakerStore';
import { useBlockModalStore } from '@/stores/useBlockModalStore';
import IconAdaptive from '@/components/IconAdaptive';
import ImageAdaptive from '@/components/ImageAdaptive';
import { CATEGORIES } from '@/(routers)/boards/editor/block/definitions/categories';
import type { NoteData } from '@/(routers)/boards/editor/block/definitions/fields/fieldPianoRoll';

const PITCHES = ['ド', 'レ', 'ミ', 'ファ', 'ソ', 'ラ', 'シ'];
const PITCH_VALUES = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];

const PITCH_CLASSES: Record<string, string> = {
    C: 'bg-color-block-speaker-pitch-c',
    D: 'bg-color-block-speaker-pitch-d',
    E: 'bg-color-block-speaker-pitch-e',
    F: 'bg-color-block-speaker-pitch-f',
    G: 'bg-color-block-speaker-pitch-g',
    A: 'bg-color-block-speaker-pitch-a',
    B: 'bg-color-block-speaker-pitch-b',
};

const OCTAVES = [
    { label: '-1オクターブ', value: -1 },
    { label: '±0オクターブ', value: 0 },
    { label: '+1オクターブ', value: 1 },
    { label: '+2オクターブ', value: 2 },
];

const DURATIONS = [
    { label: 'もっと短い', value: 75 },
    { label: '短い', value: 150 },
    { label: 'ふつうの長さ', value: 300 },
    { label: '長い', value: 450 },
    { label: 'もっと長い', value: 600 },
];

export default function Speaker() {
    const { closeModal, setOnConfirm } = useBlockModalStore();
    const { fieldRef } = useBlockModalSpeakerStore();
    const [notes, setNotes] = useState<NoteData[]>(fieldRef?.getNotes() ?? []);

    const updateNote = (index: number, updates: Partial<NoteData>) => {
        setNotes((prev) => prev.map((n, i) => (i === index ? { ...n, ...updates } : n)));
    };

    const addNote = () => {
        setNotes((prev) => [...prev, { type: 'note', pitch: 'C', octave: 0, duration: 300 }]);
    };

    const deleteNote = (index: number) => {
        setNotes((prev) => prev.filter((_, i) => i !== index));
    };

    const handleOk = useCallback(() => {
        fieldRef?.setNotes(notes);
        closeModal();
    }, [notes, fieldRef, closeModal]);

    useEffect(() => {
        setOnConfirm(handleOk);
    }, [handleOk, setOnConfirm]);

    return (
        <div className="flex flex-col h-full bg-color-block-action-modal-bg rounded-2xl overflow-hidden">
            {/* ヘッダー */}
            <div className="flex items-center px-3 h-[50px] bg-color-block-action-modal-header flex-shrink-0">
                <span className="text-xl mr-2">
                    <ImageAdaptive src="/images/block/speaker.svg" baseWidth={40} />
                </span>
                <span className="text-color-white-base font-semibold text-sm">スピーカーの音設定</span>
            </div>

            {/* ノートリスト */}
            <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-2 min-h-0">
                {notes.map((note, i) => (
                    <div
                        key={i}
                        className="flex items-center gap-2 bg-color-block-action-modal-row-bg rounded-lg px-3 py-2 flex-shrink-0"
                    >
                        <span className="text-color-block-action-modal-text-index text-xs w-4 text-center flex-shrink-0">
                            {i + 1}
                        </span>

                        <div className="flex rounded-md overflow-hidden flex-shrink-0">
                            <button
                                onClick={() => updateNote(i, { type: 'note' })}
                                className={`px-2 py-1 text-xs ${
                                    note.type === 'note'
                                        ? 'bg-color-block-action-modal-header text-color-white-base'
                                        : 'bg-color-block-action-modal-button-bg text-color-block-action-modal-text-muted'
                                }`}
                            >
                                <IconAdaptive Icon={Music4} strokeWidth={2} baseSize={16} />
                            </button>
                            <button
                                onClick={() => updateNote(i, { type: 'rest' })}
                                className={`px-2 py-1 text-xs ${
                                    note.type === 'rest'
                                        ? 'bg-color-block-action-modal-rest-active text-color-white-base'
                                        : 'bg-color-block-action-modal-button-bg text-color-block-action-modal-text-muted'
                                }`}
                            >
                                <IconAdaptive Icon={VolumeX} strokeWidth={2} baseSize={18} />
                            </button>
                        </div>

                        <div
                            className={`flex gap-1 flex-shrink-0 ${
                                note.type === 'rest' ? 'opacity-20 pointer-events-none' : ''
                            }`}
                        >
                            {PITCHES.map((label, pi) => (
                                <button
                                    key={pi}
                                    onClick={() => updateNote(i, { pitch: PITCH_VALUES[pi] })}
                                    className={`w-7 h-7 rounded text-xs font-medium ${
                                        note.pitch === PITCH_VALUES[pi]
                                            ? `${PITCH_CLASSES[PITCH_VALUES[pi]]} text-color-white-base`
                                            : 'bg-color-block-action-modal-button-bg text-color-block-action-modal-text-muted'
                                    }`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>

                        <div
                            className={`flex items-center gap-1 flex-shrink-0 ${
                                note.type === 'rest' ? 'opacity-20 pointer-events-none' : ''
                            }`}
                        >
                            <select
                                value={note.octave ?? 0}
                                onChange={(e) => updateNote(i, { octave: Number(e.target.value) })}
                                className="bg-color-block-action-modal-button-bg text-color-white-base border-none rounded text-xs px-1 py-1"
                            >
                                {OCTAVES.map((o) => (
                                    <option key={o.value} value={o.value}>
                                        {o.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <select
                            value={note.duration ?? 300}
                            onChange={(e) => updateNote(i, { duration: Number(e.target.value) })}
                            className="flex-1 bg-color-block-action-modal-button-bg text-color-white-base border-none rounded text-xs px-2 py-1"
                        >
                            {DURATIONS.map((d) => (
                                <option key={d.value} value={d.value}>
                                    {d.label}
                                </option>
                            ))}
                        </select>

                        <button
                            onClick={() => deleteNote(i)}
                            className="text-color-block-action-modal-text-index hover:text-color-block-action-modal-header text-sm px-1 flex-shrink-0"
                        >
                            <IconAdaptive Icon={X} strokeWidth={2} baseSize={16} />
                        </button>
                    </div>
                ))}
            </div>

            {/* フッター */}
            <div className="flex justify-between items-center gap-2 px-5 py-3 border-t border-color-block-action-modal-border flex-shrink-0">
                <button
                    onClick={addNote}
                    className="flex items-center pl-4 pr-5 py-2 bg-color-block-action-modal-button-bg border border-dashed border-color-block-action-modal-text-index rounded-lg text-color-block-action-modal-text-index text-xs hover:border-color-block-action-modal-header hover:text-color-block-action-modal-header transition-colors animate-pulse"
                    style={{ borderColor: CATEGORIES.action.color, color: CATEGORIES.action.color }}
                >
                    <IconAdaptive Icon={Plus} strokeWidth={2} baseSize={16} className="mr-1" />
                    音を追加
                </button>
                <div className="flex gap-2">
                    <button
                        onClick={closeModal}
                        className="px-4 py-2 bg-color-block-action-modal-cancel text-color-block-action-modal-text-gray rounded-lg text-sm"
                    >
                        キャンセル
                    </button>
                    <button
                        onClick={handleOk}
                        className="px-6 py-2 bg-color-block-action-modal-ok text-color-white-base rounded-lg text-sm font-semibold hover:bg-color-block-action-modal-ok-hover transition-colors"
                    >
                        OK
                    </button>
                </div>
            </div>
        </div>
    );
}
